import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertCircle, CheckCircle2, Clock3, Loader2 } from "lucide-react";
import {
  getGetSystemDirectoryQueryKey,
  useGetSystemDirectory,
  useTestSystemRetrievalSource,
  useUpdateRustSource,
  useUpdatePlatformSource,
} from "@tubo/api-models/hooks/retrieval-sources-api.hooks";
import type {
  RetrievalSourceConfigFieldDO,
  RetrievalSourceDirectoryResp,
  RetrievalSourceEntryResp,
  TestRetrievalSourceReqOperation,
  TestRetrievalSourceResp,
} from "@tubo/api-models/hooks/retrieval-sources-api.schemas";
import { usePermissions } from "@client/hooks/use-permissions";
import { useSecondaryNavTitle } from "@client/components/secondary-nav";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Button } from "@tubo/ui/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@tubo/ui/components/ui/card";
import { Input } from "@tubo/ui/components/ui/input";
import { Label } from "@tubo/ui/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@tubo/ui/components/ui/select";
import { Skeleton } from "@tubo/ui/components/ui/skeleton";
import { Switch } from "@tubo/ui/components/ui/switch";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/settings/data-sources")({ component: SettingsDataSources });

type ConfigValue = string | number | boolean;

function SettingsDataSources() {
  const { t } = useTranslation("systemSettings");
  const { isSuperAdmin } = usePermissions();
  const [selectedId, setSelectedId] = useState<string>("crossref");
  useSecondaryNavTitle(<span className="text-sm font-semibold">{t("dataSources.title")}</span>);
  const query = useGetSystemDirectory({ query: { enabled: isSuperAdmin, refetchOnWindowFocus: false } });

  if (!isSuperAdmin) return <p className="p-6 text-sm text-muted-foreground">{t("dataSources.permissionDenied")}</p>;
  if (query.isPending) return <LoadingState />;
  if (query.isError || !query.data?.sources?.length) return (
    <div className="space-y-3 p-6" role="alert">
      <p className="text-sm">{t("dataSources.loadFailed")}</p>
      <Button variant="outline" size="sm" onClick={() => void query.refetch()}>{t("dataSources.retry")}</Button>
    </div>
  );
  const source = query.data.sources.find(item => item.sourceId === selectedId) ?? query.data.sources[0];
  if (!source) return <LoadingState />;
  return (
    <main className="min-w-0 space-y-5 p-4 xl:p-6">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">{t("dataSources.title")}</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">{t("dataSources.description")}</p>
      </header>
      <section aria-labelledby="retrieval-source-list-title" className="space-y-3">
        <div>
          <h2 id="retrieval-source-list-title" className="text-base font-semibold">{t("dataSources.sourceDirectory")}</h2>
          <p className="text-sm text-muted-foreground">{t("dataSources.sourceDirectoryDescription")}</p>
        </div>
        <div className="grid min-w-0 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {query.data.sources.map(item => {
            const status = sourceStatus(item, t);
            return (
              <button key={item.sourceId} type="button" aria-pressed={item.sourceId === source.sourceId}
                onClick={() => setSelectedId(item.sourceId)}
                className={`min-w-0 rounded-lg border bg-card p-3 text-left transition-colors hover:bg-accent/40 ${item.sourceId === source.sourceId ? "border-primary ring-1 ring-primary" : ""}`}>
                <span className="flex min-w-0 items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{item.label}</span>
                  <Badge variant={status.variant}>{status.label}</Badge>
                </span>
                <span className="mt-2 block line-clamp-2 min-h-10 text-xs text-muted-foreground">{item.description}</span>
              </button>
            );
          })}
        </div>
      </section>
      {source.connectionType === "rust-native"
        ? <SourceDetail key={`${source.sourceId}:${source.configRevision}`} source={source} />
        : <PlatformSourceDetail key={`${source.sourceId}:${source.platformEnabled}`} source={source} />}
    </main>
  );
}

function LoadingState() {
  const { t } = useTranslation("systemSettings");
  return (
    <div className="space-y-5 p-4 xl:p-6" role="status" aria-label={t("dataSources.loading")} aria-busy="true">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-20 w-full max-w-4xl" />
      <div className="grid gap-5 lg:grid-cols-2"><Skeleton className="h-80 w-full" /><Skeleton className="h-80 w-full" /></div>
    </div>
  );
}

function PlatformSourceDetail({ source }: { source: RetrievalSourceEntryResp }) {
  const { t } = useTranslation("systemSettings");
  const cache = useQueryClient();
  const queryKey = getGetSystemDirectoryQueryKey();
  const update = useUpdatePlatformSource();
  const test = useTestSystemRetrievalSource();
  const [testQuery, setTestQuery] = useState("");
  const canTest = source.testOperations?.includes("search") ?? false;
  const busy = update.isPending || test.isPending;
  const status = sourceStatus(source, t);
  const sourceType = source.connectionType === "searxng" ? t("dataSources.searxngType")
    : source.connectionType === "tenant-api" ? t("dataSources.tenantApiType") : t("dataSources.publicApiType");
  const owner = source.ownerScope === "tenant" ? t("dataSources.tenantOwner") : t("dataSources.platformOwner");

  const setEnabled = async (enabled: boolean) => {
    try {
      const updated = await update.mutateAsync({ sourceId: source.sourceId, data: { enabled } });
      const current = cache.getQueryData<RetrievalSourceDirectoryResp>(queryKey);
      if (current?.sources) cache.setQueryData(queryKey, {
        ...current,
        sources: current.sources.map(item => item.sourceId === updated.sourceId ? updated : item),
      });
      await cache.invalidateQueries({ queryKey });
      toast.success(t("dataSources.saved"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("dataSources.saveFailed"));
    }
  };

  return (
    <div className="grid min-w-0 gap-5 lg:grid-cols-2">
      <Card className="min-w-0">
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>{source.label}</CardTitle>
            <Badge variant={status.variant}>{status.label}</Badge>
          </div>
          <CardDescription>{source.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <dl className="grid gap-x-4 gap-y-3 text-sm sm:grid-cols-2">
            <div><dt className="text-xs text-muted-foreground">{t("dataSources.connectionType")}</dt><dd>{sourceType}</dd></div>
            <div><dt className="text-xs text-muted-foreground">{t("dataSources.connectionOwner")}</dt><dd>{owner}</dd></div>
            {source.connectionId && <div className="min-w-0 sm:col-span-2">
              <dt className="text-xs text-muted-foreground">{t("dataSources.connectionId")}</dt>
              <dd className="break-all font-mono text-xs">{source.connectionId}</dd>
            </div>}
          </dl>
          {source.statusReason === "deploymentNotConfigured" && <p className="text-sm text-destructive" role="status">{t("dataSources.searxngNotConfigured")}</p>}
          {source.ownerScope === "tenant" && <p className="text-sm text-muted-foreground">{t("dataSources.tenantManagedHint")}</p>}
          {source.sourceId === "web" && <p className="text-sm text-muted-foreground">{t("dataSources.searxngDeploymentHint")}</p>}
          {source.sourceId === "chembl" && <p className="text-sm text-muted-foreground">{t("dataSources.publicApiHint")}</p>}
          <div className="flex items-start justify-between gap-4 border-t pt-4">
            <div className="space-y-1">
              <Label htmlFor={`${source.sourceId}-platform-enabled`}>{t("dataSources.platformEnable")}</Label>
              <p className="text-xs text-muted-foreground">{t("dataSources.platformEnableHint")}</p>
            </div>
            <Switch id={`${source.sourceId}-platform-enabled`} checked={source.platformEnabled} disabled={busy}
              onCheckedChange={value => void setEnabled(value)} />
          </div>
        </CardContent>
      </Card>
      <Card className="min-w-0">
        <CardHeader>
          <CardTitle>{t("dataSources.testTitle")}</CardTitle>
          <CardDescription>{canTest ? t("dataSources.testDescription") : t("dataSources.tenantManagedTestHint")}</CardDescription>
        </CardHeader>
        {canTest && <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="retrieval-platform-test-query">{t("dataSources.query")}</Label>
            <Input id="retrieval-platform-test-query" value={testQuery} maxLength={2000} disabled={busy}
              placeholder={source.sourceId === "chembl" ? "aspirin" : t("dataSources.queryPlaceholder")}
              onChange={event => { setTestQuery(event.target.value); test.reset(); }} />
          </div>
          {!source.platformEnabled && <p className="text-sm text-muted-foreground">{t("dataSources.enableBeforeTest")}</p>}
          <Button size="sm" variant="outline" disabled={busy || !testQuery.trim() || !source.platformEnabled}
            onClick={() => test.mutate({ sourceId: source.sourceId, data: { query: testQuery.trim(), operation: "search", limit: 3 } })}>
            {test.isPending && <Loader2 className="size-4 animate-spin" />}{t("dataSources.runTest")}
          </Button>
          {test.data && <TestResult result={test.data} source={source} />}
          {test.isError && <p className="text-sm text-destructive" role="alert">{t("dataSources.testFailed")}</p>}
        </CardContent>}
      </Card>
    </div>
  );
}

function SourceDetail({ source }: { source: RetrievalSourceEntryResp }) {
  const { t } = useTranslation("systemSettings");
  const cache = useQueryClient();
  const queryKey = getGetSystemDirectoryQueryKey();
  const save = useUpdateRustSource();
  const test = useTestSystemRetrievalSource();
  const [testQuery, setTestQuery] = useState("");
  const testOperations = (source.testOperations ?? []).filter(isTestOperation);
  const [operation, setOperation] = useState<TestRetrievalSourceReqOperation>(testOperations[0] ?? "search");
  const [configValues, setConfigValues] = useState<Record<string, ConfigValue>>(() =>
    parseConfigValues(source.configFields ?? [], source.configValues ?? {}));
  const [secretValues, setSecretValues] = useState<Record<string, string>>({});
  const [clearSecrets, setClearSecrets] = useState<string[]>([]);
  const configDirty = !sameConfigValues(configValues, parseConfigValues(source.configFields ?? [], source.configValues ?? {}))
    || Object.values(secretValues).some(secret => secret.trim().length > 0)
    || clearSecrets.length > 0;
  const unsupportedVersion = source.adapterVersion !== "1";
  const busy = save.isPending || test.isPending || unsupportedVersion;
  const form = useForm({
    defaultValues: {
      enabled: source.enabled,
      timeoutMs: source.timeoutMs,
      maxRequestsPerMinute: source.maxRequestsPerMinute,
    },
    onSubmit: async ({ value }) => {
      try {
        const updated = await save.mutateAsync({ sourceId: source.sourceId, data: {
          ...value,
          configValues: {
            ...Object.fromEntries(Object.entries(configValues).map(([name, fieldValue]) => [name, String(fieldValue)])),
            ...Object.fromEntries(Object.entries(secretValues).filter(([, secret]) => secret.trim())),
          },
          clearSecrets,
        } });
        const current = cache.getQueryData<RetrievalSourceDirectoryResp>(queryKey);
        if (current?.sources) cache.setQueryData(queryKey, {
          ...current,
          sources: current.sources.map(item => item.sourceId === updated.sourceId ? updated : item),
        });
        form.reset({
          enabled: updated.enabled,
          timeoutMs: updated.timeoutMs,
          maxRequestsPerMinute: updated.maxRequestsPerMinute,
        });
        setConfigValues(parseConfigValues(updated.configFields ?? [], updated.configValues ?? {}));
        setSecretValues({});
        setClearSecrets([]);
        // The revision belongs to the whole snapshot, including unchanged sources.
        await cache.invalidateQueries({ queryKey });
        toast.success(t("dataSources.saved"));
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t("dataSources.saveFailed"));
      }
    },
  });
  const status = sourceStatus(source, t);
  const testPlaceholder = testPlaceholderFor(source.sourceId, operation, t);

  return (
    <div className="grid min-w-0 gap-5 lg:grid-cols-2">
      <Card className="min-w-0">
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>{source.label}</CardTitle>
            <Badge variant={status.variant}>{status.label}</Badge>
          </div>
          <CardDescription>{source.description}</CardDescription>
          <p className="text-xs text-muted-foreground">{t("dataSources.revision", { revision: source.configRevision })} · v{source.adapterVersion}</p>
          {source.connectionId && <p className="break-all font-mono text-xs text-muted-foreground">{t("dataSources.connectionId")}: {source.connectionId}</p>}
        </CardHeader>
        <CardContent className="space-y-5">
          {source.configError && <p className="break-words text-sm text-destructive" role="alert">{source.configError}</p>}
          {unsupportedVersion && <p className="text-sm text-destructive" role="alert">
            {t("dataSources.unsupportedVersion", { version: source.adapterVersion })}
          </p>}
          <form className="space-y-5" onSubmit={event => { event.preventDefault(); void form.handleSubmit(); }}>
            <form.Field name="enabled">{field => (
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div className="space-y-1">
                  <Label htmlFor={`${source.sourceId}-enabled`}>{t("dataSources.enabled")}</Label>
                  <p className="text-xs text-muted-foreground">{t("dataSources.selectionRemainsExplicit")}</p>
                </div>
                <Switch id={`${source.sourceId}-enabled`} checked={field.state.value} disabled={busy}
                  onCheckedChange={value => { field.handleChange(value); test.reset(); }} />
              </div>
            )}</form.Field>
            <div className="space-y-4">
              {(source.configFields ?? []).map(field => <SourceConfigField key={field.name} sourceId={source.sourceId}
                field={field} value={configValues[field.name]} secretValue={secretValues[field.name] ?? ""}
                secretIsSet={source.configuredSecrets?.includes(field.name) ?? (field.name === "apiKey" && source.hasApiKey)}
                secretIsCleared={clearSecrets.includes(field.name)} busy={busy} t={t}
                onValueChange={value => setConfigValues(current => ({ ...current, [field.name]: value }))}
                onSecretChange={value => {
                  setSecretValues(current => ({ ...current, [field.name]: value }));
                  setClearSecrets(current => current.filter(name => name !== field.name));
                }}
                onClearSecretChange={value => setClearSecrets(current => value
                  ? [...new Set([...current, field.name])]
                  : current.filter(name => name !== field.name))}
                onEdited={() => test.reset()} />)}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <form.Field name="timeoutMs">{field => (
                <NumberField id={`${source.sourceId}-timeout`} label={t("dataSources.timeout")} value={field.state.value}
                  min={1000} max={60000} disabled={busy} onChange={value => { field.handleChange(value); test.reset(); }} />
              )}</form.Field>
              <form.Field name="maxRequestsPerMinute">{field => (
                <NumberField id={`${source.sourceId}-rate`} label={t("dataSources.requestsPerMinute")} value={field.state.value}
                  min={1} max={source.sourceId === "pubmed" || source.sourceId === "deepseek-search" ? 600 : source.sourceId === "pubchem" ? 300 : source.sourceId === "arxiv" ? 20 : 60}
                  disabled={busy} onChange={value => { field.handleChange(value); test.reset(); }} />
              )}</form.Field>
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t pt-4">
              <Button type="submit" size="sm" disabled={busy || (!form.state.isDirty && !configDirty)}>
                {save.isPending && <Loader2 className="size-4 animate-spin" />}{t("dataSources.save")}
              </Button>
              <span className="text-xs text-muted-foreground">{t("dataSources.applyDoesNotTest")}</span>
            </div>
          </form>
        </CardContent>
      </Card>
      <Card className="min-w-0">
        <CardHeader>
          <CardTitle>{t("dataSources.testTitle")}</CardTitle>
          <CardDescription>{t("dataSources.testDescription")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="retrieval-test-operation">{t("dataSources.operation")}</Label>
            <Select value={operation} onValueChange={value => {
              if (value && isTestOperation(value) && testOperations.includes(value)) setOperation(value);
              test.reset();
            }} disabled={busy}>
              <SelectTrigger id="retrieval-test-operation"><SelectValue /></SelectTrigger>
              <SelectContent>
                {testOperations.map(value => <SelectItem key={value} value={value}>{operationLabel(value, t)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="retrieval-test-query">{t("dataSources.query")}</Label>
            <Input id="retrieval-test-query" value={testQuery} maxLength={source.sourceId === "deepseek-search" ? 128 : 2000} disabled={busy}
              placeholder={testPlaceholder} onChange={event => { setTestQuery(event.target.value); test.reset(); }} />
          </div>
          {!source.enabled && <p className="text-sm text-muted-foreground">{t("dataSources.enableBeforeTest")}</p>}
          <Button size="sm" variant="outline" disabled={busy || !testQuery.trim() || !source.enabled}
            onClick={() => test.mutate({ sourceId: source.sourceId, data: { query: testQuery.trim(), operation, limit: 3 } })}>
            {test.isPending && <Loader2 className="size-4 animate-spin" />}{t("dataSources.runTest")}
          </Button>
          {test.data && <TestResult result={test.data} source={source} />}
          {test.isError && <p className="text-sm text-destructive" role="alert">{t("dataSources.testFailed")}</p>}
        </CardContent>
      </Card>
    </div>
  );
}

function TextField({ id, label, description, value, disabled, type = "text", placeholder, required, onChange }: {
  id: string; label: string; description: string; value: string; disabled: boolean; type?: string; placeholder?: string;
  required?: boolean; onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} value={value} placeholder={placeholder} maxLength={512} disabled={disabled} required={required}
        onChange={event => onChange(event.target.value)} />
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

function SourceConfigField({ sourceId, field, value, secretValue, secretIsSet, secretIsCleared, busy, t,
  onValueChange, onSecretChange, onClearSecretChange, onEdited }: {
  sourceId: string;
  field: RetrievalSourceConfigFieldDO;
  value: ConfigValue | undefined;
  secretValue: string;
  secretIsSet: boolean;
  secretIsCleared: boolean;
  busy: boolean;
  t: (key: string, options?: Record<string, string>) => string;
  onValueChange: (value: ConfigValue) => void;
  onSecretChange: (value: string) => void;
  onClearSecretChange: (value: boolean) => void;
  onEdited: () => void;
}) {
  const id = `${sourceId}-${field.name}`;
  if (field.fieldType === "text") return <TextField id={id} label={field.label} description={field.description}
    value={typeof value === "string" ? value : ""} disabled={busy} required={field.required}
    type={field.name.toLowerCase().includes("email") || field.name.toLowerCase() === "mailto" ? "email" : "text"}
    onChange={next => { onValueChange(next); onEdited(); }} />;
  if (field.fieldType === "number") return <NumberField id={id} label={field.label}
    value={typeof value === "number" ? value : field.minimum ?? 0} min={field.minimum ?? 0}
    max={field.maximum ?? 100_000} description={field.description} disabled={busy}
    onChange={next => { onValueChange(next); onEdited(); }} />;
  if (field.fieldType === "toggle") return (
    <div className="flex items-center justify-between gap-4 rounded-md border p-3">
      <div className="space-y-1"><Label htmlFor={id}>{field.label}</Label><p className="text-xs text-muted-foreground">{field.description}</p></div>
      <Switch id={id} checked={value === true} disabled={busy} onCheckedChange={next => { onValueChange(next); onEdited(); }} />
    </div>
  );
  if (field.fieldType === "enum") return (
    <div className="space-y-2">
      <Label htmlFor={id}>{field.label}</Label>
      <Select value={typeof value === "string" ? value : ""} onValueChange={next => {
        if (next !== null) { onValueChange(next); onEdited(); }
      }} disabled={busy}>
        <SelectTrigger id={id}><SelectValue /></SelectTrigger><SelectContent>
          {field.options?.map(option => <SelectItem key={option} value={option}>{option}</SelectItem>)}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">{field.description}</p>
    </div>
  );
  if (field.fieldType === "secret") return (
    <div className="space-y-3">
      <TextField id={id} label={field.label} description={field.description} value={secretValue} disabled={busy}
        type="password" placeholder={secretIsSet ? t("dataSources.secretSaved") : undefined}
        onChange={next => { onSecretChange(next); onEdited(); }} />
      {secretIsSet && <div className="flex items-center justify-between gap-3">
        <Label htmlFor={`${id}-clear`} className="text-sm">{t("dataSources.clearSecret")}</Label>
        <Switch id={`${id}-clear`} checked={secretIsCleared} disabled={busy} onCheckedChange={onClearSecretChange} />
      </div>}
    </div>
  );
  return <p className="text-sm text-destructive" role="alert">{t("dataSources.unsupportedField", { field: field.name })}</p>;
}

function NumberField({ id, label, value, min, max, description, disabled, onChange }: {
  id: string; label: string; value: number; min: number; max: number; description?: string; disabled: boolean; onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="number" min={min} max={max} step={1} value={value} disabled={disabled}
        onChange={event => onChange(Number(event.target.value))} />
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  );
}

function TestResult({ result, source }: { result: TestRetrievalSourceResp; source: RetrievalSourceEntryResp }) {
  const { t } = useTranslation("systemSettings");
  const Icon = result.success ? CheckCircle2 : AlertCircle;
  return (
    <section className="min-w-0 space-y-3 border-t pt-4" aria-live="polite">
      <div className="flex items-start gap-2">
        <Icon className={`mt-0.5 size-4 shrink-0 ${result.success ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`} aria-hidden="true" />
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium">{result.message}</p>
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>{t("dataSources.testStatus", { status: result.status })}</span>
            <span>{t("dataSources.testRecordCount", { count: result.recordCount })}</span>
            {result.elapsedMs !== undefined && <span className="inline-flex items-center gap-1"><Clock3 className="size-3" />{t("dataSources.elapsed", { ms: result.elapsedMs })}</span>}
            {result.configRevision !== undefined && <span>{t("dataSources.testRevision", { revision: result.configRevision })}</span>}
          </p>
        </div>
      </div>
      {result.records?.length ? <ul className="space-y-3" aria-label={t("dataSources.testResults", { source: source.label })}>
        {result.records.map(record => <li key={`${record.identifier}:${record.url}`} className="min-w-0 border-t pt-3">
          <a href={record.url} target="_blank" rel="noreferrer" className="break-words text-sm font-medium text-primary underline underline-offset-4">{record.title}</a>
          <p className="mt-1 break-all text-xs text-muted-foreground">{record.identifier}</p>
          {record.authors?.length ? <p className="mt-1 break-words text-xs text-muted-foreground">{record.authors.join(", ")}</p> : null}
          {record.publishedAt && <p className="mt-1 text-xs text-muted-foreground">{record.publishedAt}</p>}
          {record.abstractText && <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-sm text-muted-foreground">{record.abstractText}</p>}
          {record.properties?.length ? <dl className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
            {record.properties.map(property => <div key={property.name} className="min-w-0 break-words">
              <dt className="inline font-medium">{property.name}: </dt>
              <dd className="inline text-muted-foreground">{property.textValue ?? property.value}{property.unit ? ` ${property.unit}` : ""}</dd>
            </div>)}
          </dl> : null}
        </li>)}
      </ul> : null}
    </section>
  );
}

function sourceStatus(source: RetrievalSourceEntryResp, t: (key: string) => string): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } {
  if (!source.enabled) return { label: t("dataSources.disabled"), variant: "secondary" };
  if (source.ownerScope === "tenant") return { label: t("dataSources.tenantManaged"), variant: "outline" };
  if (source.statusReason === "deploymentNotConfigured") return { label: t("dataSources.notConfigured"), variant: "destructive" };
  if (source.configError) return { label: t("dataSources.applyFailed"), variant: "destructive" };
  if (!source.moduleReady) return { label: t("dataSources.serviceUnavailable"), variant: "outline" };
  if (!source.available) return { label: t("dataSources.serviceUnavailable"), variant: "destructive" };
  return { label: t("dataSources.applied"), variant: "default" };
}

function isTestOperation(value: string): value is TestRetrievalSourceReqOperation {
  return value === "search" || value === "lookup" || value === "properties";
}

function operationLabel(value: TestRetrievalSourceReqOperation, t: (key: string) => string): string {
  if (value === "search") return t("dataSources.search");
  if (value === "lookup") return t("dataSources.lookup");
  return t("dataSources.properties");
}

function testPlaceholderFor(sourceId: string, operation: TestRetrievalSourceReqOperation, t: (key: string) => string): string {
  if (operation === "lookup" || operation === "properties") {
    if (sourceId === "crossref") return "10.1038/nature12373";
    if (sourceId === "pubmed") return "12345678";
    if (sourceId === "arxiv") return "2301.00001v1";
    if (sourceId === "pubchem") return "CID:962";
  }
  return sourceId === "pubchem" ? "ethanol, CAS, or CID" : t("dataSources.queryPlaceholder");
}

function sameConfigValues(left: Record<string, ConfigValue>, right: Record<string, ConfigValue>): boolean {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every(key => left[key] === right[key]);
}

function parseConfigValues(fields: RetrievalSourceConfigFieldDO[], values: Record<string, string>): Record<string, ConfigValue> {
  const parsed: Record<string, ConfigValue> = {};
  for (const field of fields) {
    const value = values[field.name];
    if (value === undefined || field.fieldType === "secret") continue;
    if (field.fieldType === "number") {
      const number = Number(value);
      if (Number.isFinite(number)) parsed[field.name] = number;
      continue;
    }
    if (field.fieldType === "toggle") parsed[field.name] = value === "true";
    else parsed[field.name] = value;
  }
  return parsed;
}
